/**
 * Analytics Event Types and Parameters for CheckerFly
 *
 * **Purpose**: Type-safe analytics event tracking with Firebase Analytics
 *
 * **Related Work Items**:
 * - WI-003: Analytics setup (Google Analytics on landing page)
 * - WI-033: FCM integration (Firebase setup)
 * - WI-039: Firebase Analytics integration (this file)
 *
 * **Design Principles**:
 * - Event names use snake_case (Firebase Analytics convention)
 * - Event parameters are strictly typed for type safety
 * - All events documented with context and usage
 *
 * @see CheckerFly_SDD.md Section 10.1 (Observability)
 */

/**
 * Analytics event names enum
 *
 * **Naming Convention**: category.action_detail (WI-039 Section 2.1)
 * - Categories: ui, auth, error, onboarding, user
 * - Lowercase letters, numbers, underscores, dots only
 * - Max 40 characters
 * - No spaces or special characters
 *
 * @see https://firebase.google.com/docs/analytics/events
 * @see WI-039 Section 2.1 (Event Naming Convention)
 */
export enum AnalyticsEvent {
  // UI Events - Check-in lifecycle
  UI_CHECKIN_CREATE_OPENED = 'ui.checkin_create_opened',
  UI_CHECKIN_CREATE_SUBMITTED = 'ui.checkin_create_submitted',
  UI_CHECKIN_CREATE_SUCCESS = 'ui.checkin_create_success',
  UI_CHECKIN_CREATE_CANCELLED = 'ui.checkin_create_cancelled',
  UI_CHECKIN_ADVANCED_SETTINGS_OPENED = 'ui.checkin_advanced_settings_opened',

  // UI Events - Time picker
  UI_TIME_PRESET_SELECTED = 'ui.time_preset_selected',
  UI_TIME_PICKER_MANUAL_INPUT = 'ui.time_picker_manual_input',
  UI_TIME_PICKER_CLEARED = 'ui.time_picker_cleared',

  // UI Events - Check-in actions
  UI_CHECKIN_RESOLVED = 'ui.checkin_resolved',
  UI_CHECKIN_SNOOZED = 'ui.checkin_snoozed',
  UI_CHECKIN_CANCELLED = 'ui.checkin_cancelled',

  // Auth Events
  AUTH_PHONE_SIGNIN_INITIATED = 'auth.phone_signin_initiated',
  AUTH_SIGNIN_SUCCESS = 'auth.signin_success',
  AUTH_SIGNOUT = 'auth.signout',

  // Error Events
  ERROR_CHECKIN_CREATE_FAILED = 'error.checkin_create_failed',
  ERROR_AUTH_VERIFICATION_FAILED = 'error.auth_verification_failed',

  // Onboarding Events
  ONBOARDING_PHONE_SUBMITTED = 'onboarding.phone_submitted',
  ONBOARDING_PHONE_VERIFIED = 'onboarding.phone_verified',
  ONBOARDING_PROFILE_COMPLETED = 'onboarding.profile_completed',

  // User Events
  USER_PROFILE_CREATED = 'user.profile_created',

  // Notification events (Phase 3/4)
  NOTIFICATION_RECEIVED = 'notification_received',
  NOTIFICATION_ACTION_TAKEN = 'notification_action_taken',
  CHECKIN_ESCALATED = 'checkin_escalated',

  // User engagement events
  SCREEN_VIEW = 'screen_view',
  APP_OPEN = 'app_open',

  // Contact actions (Phase 4)
  CONTACT_ADDED = 'contact_added',
  CONTACT_REMOVED = 'contact_removed',
  SETTINGS_CHANGED = 'settings_changed',
}

/**
 * Base event parameters common to all events
 */
export interface BaseEventParams {
  /** ISO 8601 timestamp of when event occurred */
  timestamp?: string;
}

/**
 * Check-in created event parameters
 *
 * Tracked when user creates a new check-in
 */
export interface CheckInCreatedParams extends BaseEventParams {
  /** Check-in document ID */
  checkin_id: string;
  /** Minutes from now until trigger time (e.g., 30, 60, 120) */
  trigger_time_offset: number;
  /** Number of contacts assigned to this check-in */
  contact_count?: number;
  /** Whether user added a custom note */
  has_note?: boolean;
}

/**
 * Check-in resolved event parameters
 *
 * Tracked when check-in is marked as resolved (user checked in)
 */
export interface CheckInResolvedParams extends BaseEventParams {
  /** Check-in document ID */
  checkin_id: string;
  /** How the check-in was resolved */
  resolution_type: 'manual' | 'auto' | 'late';
  /** Minutes after trigger time (negative if resolved early) */
  resolution_delay?: number;
}

/**
 * Check-in snoozed event parameters
 *
 * Tracked when user snoozes a check-in notification
 */
export interface CheckInSnoozedParams extends BaseEventParams {
  /** Check-in document ID */
  checkin_id: string;
  /** Snooze duration in minutes */
  snooze_duration: number;
}

/**
 * Check-in escalated event parameters
 *
 * Tracked when check-in escalates to contacts (missed check-in)
 */
export interface CheckInEscalatedParams extends BaseEventParams {
  /** Check-in document ID */
  checkin_id: string;
  /** Number of contacts notified */
  contact_count: number;
  /** Minutes after trigger time when escalation occurred */
  escalation_delay?: number;
}

/**
 * Check-in cancelled event parameters
 *
 * Tracked when user cancels a scheduled check-in
 */
export interface CheckInCancelledParams extends BaseEventParams {
  /** Check-in document ID */
  checkin_id: string;
  /**
   * Check-in status at time of cancellation
   * Can be any status (scheduled, active, nudge, escalating, etc.)
   */
  previous_status: string;
}

/**
 * Notification received event parameters
 *
 * Tracked when user receives a push notification
 */
export interface NotificationReceivedParams extends BaseEventParams {
  /** Type of notification */
  notification_type: 'nudge' | 'alert' | 'resolved';
  /** Associated check-in ID */
  checkin_id?: string;
}

/**
 * Notification action taken event parameters
 *
 * Tracked when user taps a notification action button
 */
export interface NotificationActionTakenParams extends BaseEventParams {
  /** Action type (e.g., 'check_in', 'snooze', 'view_details') */
  action_type: string;
  /** Type of notification */
  notification_type: 'nudge' | 'alert' | 'resolved';
  /** Associated check-in ID */
  checkin_id?: string;
}

/**
 * Screen view event parameters
 *
 * Tracked when user navigates to a new screen
 */
export interface ScreenViewParams extends BaseEventParams {
  /** Screen name (e.g., 'check-in-list', 'check-in-create', 'settings') */
  screen_name: string;
  /** Previous screen name for navigation flow analysis */
  previous_screen?: string;
}

/**
 * App open event parameters
 *
 * Tracked when user opens the app
 */
export interface AppOpenParams extends BaseEventParams {
  /** Whether app was opened from a notification */
  from_notification?: boolean;
}

/**
 * Onboarding complete event parameters
 *
 * Tracked when user completes onboarding flow
 */
export interface OnboardingCompleteParams extends BaseEventParams {
  /** Time spent in onboarding (seconds) */
  duration_seconds?: number;
}

/**
 * Contact added event parameters
 *
 * Tracked when user adds a new contact
 */
export interface ContactAddedParams extends BaseEventParams {
  /** Contact document ID */
  contact_id: string;
  /** Relationship type (e.g., 'parent', 'partner', 'friend') */
  relationship?: string;
}

/**
 * Contact removed event parameters
 *
 * Tracked when user removes a contact
 */
export interface ContactRemovedParams extends BaseEventParams {
  /** Contact document ID */
  contact_id: string;
}

/**
 * Settings changed event parameters
 *
 * Tracked when user modifies app settings
 */
export interface SettingsChangedParams extends BaseEventParams {
  /** Setting category (e.g., 'quiet_hours', 'grace_window', 'notifications') */
  setting_category: string;
  /** Setting key (e.g., 'enabled', 'start_time', 'default_grace_window') */
  setting_key: string;
}

/**
 * Union type of all event parameter types
 *
 * Used for type-safe event logging in AnalyticsService
 */
export type AnalyticsEventParams =
  | CheckInCreatedParams
  | CheckInResolvedParams
  | CheckInSnoozedParams
  | CheckInEscalatedParams
  | CheckInCancelledParams
  | NotificationReceivedParams
  | NotificationActionTakenParams
  | ScreenViewParams
  | AppOpenParams
  | OnboardingCompleteParams
  | ContactAddedParams
  | ContactRemovedParams
  | SettingsChangedParams;

/**
 * User property names for Firebase Analytics
 *
 * Used to set persistent user properties for segmentation
 */
export enum AnalyticsUserProperty {
  USER_ID = 'user_id',
  SUBSCRIPTION_TIER = 'subscription_tier',
  ONBOARDING_COMPLETE = 'onboarding_complete',
  CONTACT_COUNT = 'contact_count',
  TIMEZONE = 'timezone',
}

/**
 * User property values type
 */
export type AnalyticsUserPropertyValue = string | number | boolean;
